import { toast } from 'sonner';

// ─────────────────────────────────────────────────────────────
// Cost estimation — approximate published provider pricing (USD).
// DEFAULT_RATES is the built-in table; teams can override it via the
// Rate Editor in Team Settings (stored per-team, merged over defaults).
// Teams pay their own providers directly with their own API keys.
// ─────────────────────────────────────────────────────────────
export const DEFAULT_RATES = {
  video: {
    kling: { perSecond: 0.14 },                                       // Kling 3.0 ~ $0.08–0.17/s
    veo: { perSecond: 0.40 },                                         // Veo 3.1 Standard (720p/1080p)
    seedance: { perSecond: { '480p': 0.10, '720p': 0.23, '1080p': 0.58 } }, // Seedance 2.5 token estimate
  },
  image: {
    gemini: 0.04,   // Gemini image (Nano Banana) ~ per 1K image
    openai: 0.04,   // GPT Image 1 (medium)
  },
};

// Kept for backward compatibility with earlier imports.
export const VIDEO_RATES = DEFAULT_RATES.video;
export const IMAGE_RATES = DEFAULT_RATES.image;

// Merge a (possibly partial) override table over the defaults.
export function mergeRates(overrides) {
  if (!overrides || typeof overrides !== 'object') return DEFAULT_RATES;
  const v = { ...DEFAULT_RATES.video };
  if (overrides.video) {
    for (const p of Object.keys(DEFAULT_RATES.video)) {
      if (overrides.video[p] == null) continue;
      const dp = DEFAULT_RATES.video[p].perSecond;
      const op = overrides.video[p].perSecond;
      v[p] = { perSecond: (typeof dp === 'object') ? { ...dp, ...(op || {}) } : (op ?? dp) };
    }
  }
  const img = { ...DEFAULT_RATES.image, ...(overrides.image || {}) };
  return { video: v, image: img };
}

export function estimateVideoCost(provider, duration, resolution = '720p', rates = DEFAULT_RATES) {
  const r = (rates?.video || DEFAULT_RATES.video)[provider];
  if (!r) return null;
  const per = typeof r.perSecond === 'object'
    ? (r.perSecond[resolution] ?? r.perSecond['720p'])
    : r.perSecond;
  return per * (Number(duration) || 0);
}

// frame: 'first' | 'last' | 'both'
export function estimateFrameCost(provider, frame = 'both', rates = DEFAULT_RATES) {
  const per = (rates?.image || DEFAULT_RATES.image)[provider] ?? DEFAULT_RATES.image.gemini;
  const count = frame === 'both' ? 2 : 1;
  return per * count;
}

// Cost of N generated images (frames + references), image models are ~same price.
export function estimateImagesCost(count, rates = DEFAULT_RATES) {
  const per = (rates?.image || DEFAULT_RATES.image).gemini ?? 0.04;
  return per * (Number(count) || 0);
}

export function formatCost(v) {
  if (v == null) return '';
  if (v <= 0) return '$0.00';
  return v < 0.01 ? '<$0.01' : `$${v.toFixed(2)}`;
}

// ─────────────────────────────────────────────────────────────
// Failure explanation — turn a raw provider/API error into a
// friendly, actionable hint so users understand what went wrong.
// ─────────────────────────────────────────────────────────────
export function failureHint(error) {
  const e = (error || '').toString().toLowerCase();
  if (!e) return 'Generation failed. Please try again.';
  if (/insufficient|balance|credit|quota|billing|payment|fund|exceeded|limit reached|out of/.test(e))
    return 'This usually means your provider account is out of credits or has hit a usage limit. Top up or check your plan with that provider, then try again.';
  if (/api key|apikey|unauthor|401|403|invalid key|authentication|forbidden|permission/.test(e))
    return 'This usually means the provider API key is missing, invalid, or lacks permission. Check the key in Team Settings, then try again.';
  if (/timed out after/.test(e) && /kling/.test(e))
    return 'Kling is slow right now (longer clips with sound can take 20+ min). The clip may still finish on their side — reopening this scene later will recover it automatically. Otherwise retry, ideally with a shorter duration.';
  if (/timeout|timed out|504|deadline/.test(e))
    return 'The provider took too long to respond. This is usually temporary — try generating again.';
  if (/rate limit|429|too many/.test(e))
    return "You're sending requests too quickly for this provider. Wait a moment, then try again.";
  if (/safety|moderation|policy|blocked|nsfw|content/.test(e))
    return 'The provider blocked this request for content-policy reasons. Adjust the prompt or reference images, then try again.';
  return 'Generation failed. Review the error above; if it mentions your provider account, check your key and credits in Team Settings.';
}

// ─────────────────────────────────────────────────────────────
// Deep links — copy a shareable URL to the clipboard.
// ─────────────────────────────────────────────────────────────
export async function copyShareLink(path, label = 'Link') {
  const url = `${window.location.origin}${path}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success(`${label} copied to clipboard`);
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Could not copy link');
    }
  }
  return url;
}

export async function copyRawText(text, label = 'Link') {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(`${label} copied to clipboard`);
    } catch { toast.error('Could not copy'); }
  }
}
